/* Copyright © 2026 YOUR GROWTH 365 (Jonas Brössel) — Proprietary. See LICENSE in repo root. */
/* =============================================================================
   VIEWS / ADMIN / ORDER-RECEIPT-SUMMARY — „was ist an dieser Rechnung schon
   bezahlt?" (S248)
   =============================================================================
   Seit Cut D kann eine Bestellung teilweise bezahlt sein: der Gast zahlt am
   Handy seine Positionen, der Rest bleibt offen. Die Kanban-Karte zeigte
   trotzdem den vollen Betrag und „Noch nicht bezahlt" — der Service sah 21,90 €
   und hätte 21,90 € kassiert, obwohl 10,00 € längst da waren.

   Diese Datei hält das Wissen an EINER Stelle:

   · `refreshReceiptSummaries(orderIds)` lädt die Belege ALLER sichtbaren
     Bestellungen in EINER Abfrage. Eine Abfrage je Karte wäre bei einem vollen
     Board ein Dutzend Roundtrips pro Render.
   · `receiptSummary(orderId)` ist der synchrone Zugriff für den Renderer —
     Karten werden synchron gebaut, ein `await` mitten im Markup gibt es nicht.
   · Die Label-Logik ist pur und getestet; sie kennt kein DOM.

   Realtime: eine Beleg-Änderung stupst seit phase_182 `orders.updated_at` an,
   also fällt sie in das bestehende `orders`-Abo. Ohne das gäbe es für eine
   Teilzahlung überhaupt kein Ereignis — `order_receipts` hat kein eigenes Abo
   (kein je Mandant filterbarer Kanal, dasselbe Muster wie `order_items`).
============================================================================ */

import { fmtEUR } from '../../utils/helpers.js';

/** orderId → { paidCents, paidCount, holdCents, holdCount, onlyOnline } */
const _summaries = new Map();
let _inflight = null;

/* -------------------------------------------------------------------------- */
/* Pure                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Pure — Belegzeilen einer Bestellung zu einer Zusammenfassung verdichten.
 *
 * Bestandsbelege (vor phase_179) tragen kein `paymentStatus`; sie sind bezahlt,
 * sonst gäbe es sie nicht. Eine abgelaufene Haltung zählt weder als bezahlt noch
 * als laufend — ihre Positionen sind längst wieder frei.
 */
export function summarizeReceipts(rows, nowMs) {
  const now = Number.isFinite(nowMs) ? nowMs : Date.now();
  const out = { paidCents: 0, paidCount: 0, holdCents: 0, holdCount: 0, onlyOnline: true };
  for (const r of (Array.isArray(rows) ? rows : [])) {
    if (!r) continue;
    const status = r.paymentStatus || r.payment_status || 'paid';
    const cents  = Number(r.totalGrossCents != null ? r.totalGrossCents : r.total_gross_cents) || 0;
    if (status === 'paid') {
      out.paidCents += cents;
      out.paidCount += 1;
      const method = r.paymentMethod || r.payment_method || '';
      if (method !== 'stripe') out.onlyOnline = false;
    } else if (status === 'pending') {
      const exp = r.holdExpiresAt || r.hold_expires_at;
      if (!exp || new Date(exp).getTime() > now) {
        out.holdCents += cents;
        out.holdCount += 1;
      }
    }
  }
  if (!out.paidCount) out.onlyOnline = false;
  return out;
}

/**
 * Pure — was steht auf der Karte?
 *
 * `null`, wenn es nichts zu erzählen gibt (keine Belege) — dann bleibt die Karte
 * genau so, wie sie immer war. Sonst: der offene Rest als Kopfbetrag und eine
 * Zeile, die sagt, was schon bezahlt ist.
 *
 * Das 💳 steht nur, wenn AUSSCHLIESSLICH online bezahlt wurde. Ein bar
 * kassierter Teilbeleg ist keine Kartenzahlung, und ein Symbol, das je nach
 * Datenlage etwas anderes bedeutet, ist schlechter als keins.
 *
 * @returns {{openCents:number, paidCents:number, text:string, online:boolean}|null}
 */
export function partialPayView(summary, totalCents) {
  const s = summary || {};
  const paid = Number(s.paidCents) || 0;
  const hold = Number(s.holdCents) || 0;
  if (paid <= 0 && hold <= 0) return null;

  const total = Math.max(0, Number(totalCents) || 0);
  const open  = Math.max(0, total - paid);
  const online = paid > 0 && !!s.onlyOnline;

  const parts = [];
  if (paid > 0) parts.push(`${online ? '💳 ' : ''}${fmtEUR(paid / 100)} bezahlt`);
  if (hold > 0) parts.push(`${fmtEUR(hold / 100)} zahlt gerade`);
  if (open > 0) parts.push(`${fmtEUR(open / 100)} offen`);

  return { openCents: open, paidCents: paid, holdCents: hold, online, text: parts.join(' · ') };
}

/* -------------------------------------------------------------------------- */
/* Zugriff + Laden                                                             */
/* -------------------------------------------------------------------------- */

/** Synchroner Zugriff für den Renderer. `null` = noch nichts geladen. */
export function receiptSummary(orderId) {
  return _summaries.get(String(orderId)) || null;
}

/**
 * Sichtbare Zusammenfassung einer Bestellung (oder `null`).
 *
 * S249 (B1) — bei einer BEZAHLTEN Bestellung bleibt der Kopfbetrag der
 * Gesamtbetrag. Der offene Rest ist dann 0, und „0,00 €" auf einer Erledigt-
 * Karte sagt nichts: die Frage „was ist hier noch zu kassieren" stellt sich
 * nicht mehr, die Frage „worum ging es" schon. Die Zeile „X € bezahlt" bleibt.
 */
export function partialPayFor(order) {
  if (!order || !order.id) return null;
  const total = (order.totalGrossCents != null ? order.totalGrossCents : order.total_gross_cents) || 0;
  const view = partialPayView(receiptSummary(order.id), total);
  if (!view) return null;
  const paid = (order.paymentStatus || order.payment_status) === 'paid';
  return paid ? { ...view, openCents: total, settled: true } : { ...view, settled: false };
}

/** Testhilfe + Reset beim Mandantenwechsel. */
export function _setSummaryForTests(orderId, summary) {
  if (summary) _summaries.set(String(orderId), summary);
  else _summaries.delete(String(orderId));
}
export function _clearSummaries() { _summaries.clear(); }

/**
 * Belege für mehrere Bestellungen nachladen — EINE Abfrage.
 *
 * Liefert `true`, wenn sich etwas geändert hat; der Aufrufer entscheidet dann,
 * ob er neu zeichnet. Ein Re-Render bei jedem Tick ohne Änderung wäre genau das
 * Flackern, das die Karten sonst überall vermeiden.
 */
export async function refreshReceiptSummaries(orderIds) {
  const ids = Array.from(new Set((orderIds || []).filter(Boolean).map(String)));
  if (!ids.length) return false;
  const st = window.App && window.App.storage && window.App.storage.orders;
  if (!st || typeof st.listReceiptsForOrders !== 'function') return false;
  if (_inflight) return _inflight;

  _inflight = (async () => {
    let changed = false;
    try {
      const rows = await st.listReceiptsForOrders(ids);
      const byOrder = new Map();
      for (const r of (rows || [])) {
        const oid = String(r.orderId || r.order_id || '');
        if (!oid) continue;
        if (!byOrder.has(oid)) byOrder.set(oid, []);
        byOrder.get(oid).push(r);
      }
      for (const id of ids) {
        const next = summarizeReceipts(byOrder.get(id) || []);
        const prev = _summaries.get(id);
        if (!prev || prev.paidCents !== next.paidCents || prev.holdCents !== next.holdCents
            || prev.paidCount !== next.paidCount || prev.holdCount !== next.holdCount
            || prev.onlyOnline !== next.onlyOnline) {
          _summaries.set(id, next);
          changed = true;
        }
      }
    } catch (e) {
      console.warn('[order-receipt-summary] Belege nicht ladbar:', e && e.message);
    } finally {
      _inflight = null;
    }
    return changed;
  })();
  return _inflight;
}
